import {isCopilot} from '@github-ui/assignees/copilot-user'
import {copyText} from '@github-ui/copy-to-clipboard'
import {CommandActionListItem, ScopedCommands} from '@github-ui/ui-commands'
import {BlockedIcon, KebabHorizontalIcon, LinkIcon, PencilIcon, QuoteIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton} from '@primer/react'
import {useState} from 'react'
import {
  OrganizationBlockDialog,
  OrganizationUnblockDialog,
  PersonalBlockDialog,
  PersonalUnblockDialog,
} from './ConversationItemBlockDialogs'

interface ConversationItemActionsProps {
  authorLogin: string
  contentId: string
  contentUrl: string
  link: string
  onEdit?: () => void
  onQuoteReply?: () => void
  organizationLogin?: string
  viewerCanBlockFromOrg: boolean
  viewerCanBlockPersonally?: boolean
  viewerCanUnblockFromOrg: boolean
  viewerCanUnblockPersonally?: boolean
}

export function ConversationItemActions({
  authorLogin,
  contentId,
  contentUrl,
  link,
  onEdit,
  onQuoteReply,
  organizationLogin,
  viewerCanBlockFromOrg,
  viewerCanBlockPersonally = false,
  viewerCanUnblockFromOrg,
  viewerCanUnblockPersonally = false,
}: ConversationItemActionsProps) {
  const [isBlockFromOrgOpen, setIsBlockFromOrgOpen] = useState(false)
  const [isPersonalBlockOpen, setIsPersonalBlockOpen] = useState(false)
  const [isPersonalUnblockOpen, setIsPersonalUnblockOpen] = useState(false)
  const [isUnblockFromOrgOpen, setIsUnblockFromOrgOpen] = useState(false)
  const canBlockPersonally = viewerCanBlockPersonally && !isCopilot(authorLogin)
  const canBlockFromOrg = viewerCanBlockFromOrg && !!organizationLogin && !isCopilot(authorLogin)
  const canUnblockFromOrg = viewerCanUnblockFromOrg && !!organizationLogin
  return (
    <>
      <PersonalBlockDialog
        contentId={contentId}
        authorLogin={authorLogin}
        contentUrl={contentUrl}
        isOpen={isPersonalBlockOpen}
        onClose={() => setIsPersonalBlockOpen(false)}
      />
      <PersonalUnblockDialog
        authorLogin={authorLogin}
        isOpen={isPersonalUnblockOpen}
        onClose={() => setIsPersonalUnblockOpen(false)}
      />
      {organizationLogin && (
        <OrganizationBlockDialog
          contentId={contentId}
          authorLogin={authorLogin}
          contentUrl={contentUrl}
          isOpen={isBlockFromOrgOpen}
          organizationLogin={organizationLogin}
          onClose={() => setIsBlockFromOrgOpen(false)}
        />
      )}
      {organizationLogin && (
        <OrganizationUnblockDialog
          organizationLogin={organizationLogin}
          authorLogin={authorLogin}
          contentId={contentId}
          isOpen={isUnblockFromOrgOpen}
          onClose={() => setIsUnblockFromOrgOpen(false)}
        />
      )}
      <ActionMenu>
        <ActionMenu.Anchor>
          <IconButton icon={KebabHorizontalIcon} variant="invisible" size="small" aria-label="More actions" />
        </ActionMenu.Anchor>

        <ActionMenu.Overlay align="end">
          <ActionList>
            {onQuoteReply && (
              <CommandActionListItem
                commandId="pull-requests:quote-reply"
                onSelect={onQuoteReply}
                leadingVisual={<QuoteIcon />}
              >
                Quote reply
              </CommandActionListItem>
            )}
            <ActionList.Item onSelect={() => copyText(link)}>
              <ActionList.LeadingVisual>
                <LinkIcon />
              </ActionList.LeadingVisual>
              Copy link
            </ActionList.Item>
            {onEdit && (
              <ActionList.Item onSelect={onEdit}>
                <ActionList.LeadingVisual>
                  <PencilIcon />
                </ActionList.LeadingVisual>
                Edit
              </ActionList.Item>
            )}
            {(canBlockPersonally || viewerCanUnblockPersonally || canBlockFromOrg || canUnblockFromOrg) && (
              <ActionList.Divider />
            )}
            {canBlockPersonally && (
              <ActionList.Item onSelect={() => setIsPersonalBlockOpen(true)}>
                <ActionList.LeadingVisual>
                  <BlockedIcon />
                </ActionList.LeadingVisual>
                Block user
              </ActionList.Item>
            )}
            {viewerCanUnblockPersonally && (
              <ActionList.Item onSelect={() => setIsPersonalUnblockOpen(true)}>
                <ActionList.LeadingVisual>
                  <BlockedIcon />
                </ActionList.LeadingVisual>
                Unblock user
              </ActionList.Item>
            )}
            {!canBlockPersonally && !viewerCanUnblockPersonally && canBlockFromOrg && (
              <ActionList.Item onSelect={() => setIsBlockFromOrgOpen(true)}>
                <ActionList.LeadingVisual>
                  <BlockedIcon />
                </ActionList.LeadingVisual>
                Block user
              </ActionList.Item>
            )}
            {!canBlockPersonally && !viewerCanUnblockPersonally && canUnblockFromOrg && (
              <ActionList.Item onSelect={() => setIsUnblockFromOrgOpen(true)}>
                <ActionList.LeadingVisual>
                  <BlockedIcon />
                </ActionList.LeadingVisual>
                Unblock user
              </ActionList.Item>
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </>
  )
}

export function ConversationItemActionsProvider({
  onQuoteReply,
  children,
}: React.PropsWithChildren<Pick<ConversationItemActionsProps, 'onQuoteReply'>>) {
  return <ScopedCommands commands={{'pull-requests:quote-reply': onQuoteReply}}>{children}</ScopedCommands>
}
